import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import Editor from 'ckeditor5-custom-build';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';

import { LoadingStatus } from '../../../../core/domain/events/state_provider';
import UniqueEntityID from '../../../../core/domain/unique_entity_id';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { PatientGroup } from '../../../../data/dietitian/domain/patient_group/patient_group';
import { Patient } from '../../../../data/patient/domain/patient';
import {
  PredefinedTask,
  TaskTargetType,
} from '../../../../data/todo/domain/predefined-task';
import { Task, TaskProps } from '../../../../data/todo/domain/task';
import { ToDo } from '../../../../data/todo/domain/todo';
import { PatientSelectorComponent } from '../../../dashboard/components/patient-selector/patient-selector.component';
import { TaskModalComponent } from '../task-modal/task-modal.component';
import { ToDoModalFacade } from './todo-modal.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-todo-modal',
  templateUrl: './todo-modal.component.html',
  styleUrls: ['./todo-modal.component.scss'],
  providers: [ToDoModalFacade],
})
export class ToDoModalComponent implements OnInit {
  @Input() toDo: ToDo | undefined;
  @Output() onValidate = new EventEmitter<ToDo>();
  @Output() onCancel = new EventEmitter<void>();
  taskTargetType: TaskTargetType | undefined = undefined;
  predefinedTasks: PredefinedTask[];
  dateStart: string | undefined;
  withPatient: boolean;
  isLoading = false;
  Editor = Editor;
  config = {
    placeholder: 'Veuillez saisir la description de votre tâche...',
    toolbar: [
      'bold',
      'italic',
      'underline',
      'Strikethrough',
      '|',
      'fontsize',
      'fontcolor',
      'highlight',
      '|',
      'alignment',
      'BlockQuote',
      'bulletedList',
    ],
  };
  set task(value: Task | PredefinedTask | undefined | '+') {
    this._task = value;
    if (this.toDo && this._task && this._task !== '+') {
      this.toDo.task = this._task as Task | PredefinedTask;
    }
  }
  get task(): Task | PredefinedTask | undefined | '+' {
    return this._task;
  }
  private _task: Task | PredefinedTask | undefined | '+' = undefined;
  private dietitian: Dietitian | undefined;

  constructor(
    private bsModalRef: BsModalRef,
    private facade: ToDoModalFacade,
    private modalService: BsModalService,
  ) {
    this.facade.dietitianState$.subscribe((state) => {
      if (state && state.loading === LoadingStatus.COMPLETE) {
        this.dietitian = state.entity;
      }
    });
  }

  ngOnInit(): void {
    this.withPatient = !!this.toDo && !!this.toDo.target;

    this.facade.findAllPredefinedTasks().then((tasks) => {
      this.predefinedTasks = tasks;
      if (this.toDo) {
        if (this.isUpdateMode()) {
          const expected = this.predefinedTasks.filter(
            (t) => t.id.toString() === this.toDo?.task?.id.toString(),
          );
          if (expected.length > 0) {
            this._task = expected[0];
          } else {
            this._task = undefined;
          }

          this.dateStart = moment(this.toDo.dateStart).format('YYYY-MM-DD');
        }
        this.taskTargetType = this.targetTypeOfTodo();
      }
    });
  }

  isUpdateMode() {
    return this.toDo && this.toDo.id && this.toDo.id.toString() !== '_NEW_';
  }

  cancel() {
    this.onCancel.emit();
    this.hideModal();
  }

  async validate() {
    if (this.toDo) {
      this.isLoading = true;
      await this.facade.saveToDo(this.toDo);
      this.onValidate.emit(this.toDo);
      this.isLoading = false;
      this.hideModal();
    }
  }

  enableValidate() {
    return (
      this.toDo &&
      this.toDo.dietitianId &&
      this.toDo.task &&
      this.toDo.dateStart &&
      this.toDo.description &&
      (!this.toDo.task.delay || (this.toDo.task.delay && this.toDo.dateEnd))
    );
  }

  private hideModal() {
    this.bsModalRef.hide();
  }

  getLabelSelectTask() {
    return 'Sélectionner une tâche';
  }

  doChangeTask(task: Task | PredefinedTask | '+') {
    if (task === '+') {
      const initialState: ModalOptions = {
        class: 'modal-dialog-centered',
        backdrop: 'static',
        initialState: {
          task: Task.create(
            {
              targetType: this.targetTypeOfTodo(),
              description: '',
              status: 'ACTIVE',
              dietitianId: this.toDo?.dietitianId,
              delay: {
                unit: 'DAY',
                value: 1,
              },
            } as TaskProps,
            new UniqueEntityID('_NEW_'),
          ),
        },
      };
      const taskModal = this.modalService.show(
        TaskModalComponent,
        initialState,
      );

      const modalSubscription = taskModal.onHidden?.subscribe((next) => {
        if (next === 'esc') {
          this.task = undefined;
        }
        modalSubscription?.unsubscribe();
      });

      if (taskModal.content instanceof TaskModalComponent) {
        firstValueFrom(taskModal.content.onValidate).then((newTask) => {
          this.task = newTask;
          this.setDateAndTime();
        });
        firstValueFrom(taskModal.content.onCancel).then(
          () => (this.task = undefined),
        );
      }
    } else {
      this.setDateAndTime();
    }
  }

  onDateStartChange(target: any) {
    if (this.toDo) {
      this.toDo.dateStart = new Date(target.value);
      this.toDo.dateStart.setHours(12, 0);
      this.dateStart = moment(this.toDo.dateStart).format('YYYY-MM-DD');
    }
  }

  pasteDate(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text').trim();
    if (this.toDo && pastedText) {
      const match = pastedText.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!match) {
        return;
      }
      // eslint-disable-next-line prefer-const
      let [, day, month, year] = match;
      day = day.padStart(2, '0');
      month = month.padStart(2, '0');
      this.toDo.dateEnd = new Date(`${year}-${month}-${day}`);
    }
  }

  filteredPredefinedTasks(predefinedTasks: PredefinedTask[]): PredefinedTask[] {
    if (predefinedTasks) {
      return predefinedTasks.filter((task) => {
        if (this.toDo && !this.toDo.target) {
          return true;
        }
        return (
          task.targetType === 'PATIENT' ||
          task.targetType === 'SELECTION_OF_PATIENTS'
        );
      });
    }
    return [];
  }

  getLabelTarget(target: Patient | Patient[] | undefined) {
    if (target) {
      if (Array.isArray(target)) {
        return 'Une sélection de ' + target.length + ' patients';
      } else {
        return target.firstName + ' ' + target.lastName;
      }
    }
    return '';
  }

  patientSelector() {
    const initialState: ModalOptions = {
      class: 'modal-lg modal-dialog-centered',
      initialState: {
        dietitian: this.dietitian,
      },
    };
    const patientSelector = this.modalService.show(
      PatientSelectorComponent,
      initialState,
    );
    if (patientSelector.content instanceof PatientSelectorComponent) {
      firstValueFrom(patientSelector.content.onSelect).then((patient) => {
        if (this.toDo) {
          this.toDo.target = patient;
        }
      });
    }
  }

  isNameable(task: Task | PredefinedTask | undefined | '+'): boolean {
    if (task instanceof PredefinedTask) {
      return task.isNameable;
    }
    return false;
  }

  private targetTypeOfTodo() {
    if (this.toDo && this.toDo.target) {
      if (
        Array.isArray(this.toDo.target) ||
        this.toDo.target instanceof PatientGroup
      ) {
        return 'SELECTION_OF_PATIENTS';
      } else {
        if (this.toDo.target instanceof Patient) {
          return 'PATIENT';
        }
      }
    }
    return 'NOTHING';
  }

  private setDateAndTime() {
    if (this.toDo) {
      const m = moment(this.toDo.dateStart);
      this.dateStart = m.format('YYYY-MM-DD');
    }
  }
}
